import React, { useState, useEffect, useReducer } from "react";
import Image from "next/legacy/image";
import { useRouter } from "next/router";
import moment from "moment";

import { readDocument, updateDocument } from "@/lib/db";
import { useAuth } from "@/lib/auth";
import SignUpSignIn from "@/components/user/SignUpSignIn";
import Button from "@/ui/Button";
import Checkbox from "@/ui/Checkbox";

import HomeHeader from "@/components/home/HomeHeader";

import SeoLinks from "@/components/home/SeoLinks";
import HomeFooter from "@/components/home/HomeFooter";

import Breadcrumbs from "@/components/common/Breadcrumbs";
import { imageLoader } from "@/utils/image";

/*
COOKING AND DINING
1. I cook all my own meals. O True (0) O False (1)
2. I only prepare simple meals. O True (0) O False (1)
3. I no longer cook.
O True (2) O False (0)
4. I never cooked.
O True (0) O False (2)
5. I need assistance with feeding myself or ordering food. O True (5) O False (0)
6. I have left the stove or oven on. O True (2) O False (0)
7. I have forgotten to turn off running water. O True (2) O False (0)
8. I am aware of when I’m hungry or need to eat. O True (0 O False (5)

OUTCOME:
0-2 = Independent living
3-8= Assisted Living
8+= Memory Care
__________________________________________________________________


  MEDICATION MANAGEMENT Check all that apply:
I organize all my own medications O True (0) O False (1)
A friend or family member helps me put my medications into a pill organizer. O True (1) O False (0)
I always remember to take my medications. O True (0) O False (1)
I need help or oversight with taking medications. O True (2) O False (0)
I have medications that need to be administered by someone else or a nurse. O True (2) O False (0)
I need help with my oxygen tank (if you have one). O True (2) O False (1)
I have been diagnosed or suspect I have a condition that will eventually require memory care. O True (5) O False (0)
OUTCOME:
0 = Independent living 1-9= Assisted Living 10+= Memory Care
__________________________________________________________________

MOBILITY
Check all that apply:
I need someone to help me get into a chair or bed O True (1) O False (0)
I often need two people to help me get into a chair or bed. O True (1) O False (0)
I would physically be able to evacuate a building independently. O True (0) O False (1)
If alarms were sounding in the building, I would understand what to do and evacuate if necessary. O True (0) O False (11)
I am able to move around my living space independently. O True (0) O False (1)
I have restless behavior during the day or night. O True (5) O False (0)
I am only mobile with a walker, cane, or wheelchair. O True (1) O False (0)
OUTCOME:
0 = Independent living 1-10= Assisted Living 11+= Memory Care
__________________________________________________________________

PERSONAL CARE
Check all that apply:
I manage my own bowel & bladder needs without assistance. O True (0) O False (1)
I am able to shower alone. O True (0) O False (1)
I can wash my own feet. O True (0) O False (1)
I always remember to bathe or change my clothes. O True (0) O False (3)
I have a short temper and get frustrated easily. O True (3) O False (0)
I am able to comb my hair and/or shave myself. O True (0) O False (1)
I am able to manage my own dental hygiene. O True (0) O False (1)
I can manage my own hearing aids. O True (0) O False (1)
I am able to manage my morning routine of getting dressed and ready for the day. O True (0) O False (1)
I am able to manage my evening routine of getting undressed and ready for bed. O True (0) O False (1)
I need assistance with socks, shoes, or supports. O True (0) O False (1)
OUTCOME
0-1 = Independent Living 2-9= Assisted Living 10+= Memory Care
__________________________________________________________________

LIFESTYLE
Check all that apply:
I do all my own shopping and errands. O True (0) O False (1)
I am no longer allowed to have a driver’s license. O True (1) O False (0)
I can leave my home without getting lost or confused. O True (0) O False (11)
I am able to manage my own finances and cash. O True (0) O False (1)
I repeat myself or fixate on topics. O True (5) O False (0)
I use a cellular phone. O True (0) O False (1)
I have been the victim of fraud or other crimes. O True (1) O False (0)
I say or do things that are considered inappropriate. O True (0) O False (5)
I wash and fold my own laundry. O True (0) O False (1)
I require help with housekeeping. O True (1) O False (0)
I clean my refrigerator and remove my garbage regularly. O True (0) O False (5)
I am able to converse with others and be understood. O True (0) O False (5)
OUTCOME:
0-2 = Independent living 3-9= Assisted Living 10+= Memory Care
*/

const defaultAssessment = [
    {
        index: 1,
        section: "Cooking and Dining",
        thresholds: { independent: 0, assisted: 3, memory: 8 },
        questions: [
            { question: "I cook all my own meals.", answer: null, weight: { t: 0, f: 1 } },
            {
                question: "I only prepare simple meals.",
                answer: null,
                weight: { t: 0, f: 1 },
            },
            {
                question: "I no longer cook.",
                answer: null,
                weight: { t: 2, f: 0 },
            },
            { question: "I never cooked.", answer: null, weight: { t: 0, f: 2 } },
            {
                question: "I need assistance with feeding myself or ordering food.",
                answer: null,
                weight: { t: 5, f: 0 },
            },
            {
                question: "I have left the stove or oven on.",
                answer: null,
                weight: { t: 2, f: 0 },
            },
            {
                question: "I have forgotten to turn off running water.",
                answer: null,
                weight: { t: 2, f: 0 },
            },
            {
                question: "I am aware of when I’m hungry or need to eat.",
                answer: null,
                weight: { t: 0, f: 5 },
            },
        ],
    },
    {
        index: 2,
        section: "Medication Management",
        thresholds: { independent: 0, assisted: 1, memory: 10 },
        questions: [
            {
                question: "I organize all my own medications",
                answer: null,
                weight: { t: 0, f: 1 },
            },
            {
                question:
                    "A friend or family member helps me put my medications into a pill organizer.",
                answer: null,
                weight: { t: 1, f: 0 },
            },
            {
                question: "I always remember to take my medications.",
                answer: null,
                weight: { t: 0, f: 1 },
            },
            {
                question: "I need help or oversight with taking medications.",
                answer: null,
                weight: { t: 2, f: 0 },
            },
            {
                question:
                    "I have medications that need to be administered by someone else or a nurse.",
                answer: null,
                weight: { t: 2, f: 0 },
            },
            {
                question: "I have an oxygen tank.",
                answer: null,
                weight: { t: 1, f: 0 },
            },
            {
                question: "I need help with my oxygen tank (if you have one).",
                answer: null,
                weight: { t: 1, f: 0 },
            },
            /*{
                question: "I need help with my oxygen tank (if you have one).",
                answer: null,
                weight: { t: 2, f: 1 },
            },*/
            {
                question:
                    "I have been diagnosed or suspect I have a condition that will eventually require memory care.",
                answer: null,
                weight: { t: 5, f: 0 },
            },
        ],
    },
    {
        index: 3,
        section: "Mobility",
        thresholds: { independent: 0, assisted: 1, memory: 11 },
        questions: [
            {
                question: "I need someone to help me get into a chair or bed.",
                answer: null,
                weight: { t: 1, f: 0 },
            },
            {
                question: "I often need two people to help me get into a chair or bed.",
                answer: null,
                weight: { t: 1, f: 0 },
            },
            {
                question:
                    "I would physically be able to evacuate a building independently.",
                answer: null,
                weight: { t: 0, f: 1 },
            },
            {
                question:
                    "If alarms were sounding in the building, I would understand what to do and evacuate if necessary.",
                answer: null,
                weight: { t: 0, f: 11 },
            },
            {
                question: "I am able to move around my living space independently.",
                answer: null,
                weight: { t: 0, f: 1 },
            },
            {
                question: "I have restless behavior during the day or night.",
                answer: null,
                weight: { t: 5, f: 0 },
            },
            {
                question: "I am only mobile with a walker, cane, or wheelchair.",
                answer: null,
                weight: { t: 1, f: 0 },
            },
        ],
    },
    {
        index: 4,
        section: "Personal Care",
        thresholds: { independent: 0, assisted: 2, memory: 10 },
        questions: [
            {
                question: "I manage my own bowel & bladder needs without assistance.",
                answer: null,
                weight: { t: 0, f: 1 },
            },
            {
                question: "I am able to shower alone.",
                answer: null,
                weight: { t: 0, f: 1 },
            },
            { question: "I can wash my own feet.", answer: null, weight: { t: 0, f: 1 } },
            {
                question: "I always remember to bathe or change my clothes",
                answer: null,
                weight: { t: 0, f: 3 },
            },
            {
                question: "I have a short temper and get frustrated easily.",
                answer: null,
                weight: { t: 3, f: 0 },
            },
            {
                question: "I am able to comb my hair and/or shave myself.",
                answer: null,
                weight: { t: 0, f: 1 },
            },
            {
                question: "I am able to manage my own dental hygiene.",
                answer: null,
                weight: { t: 0, f: 1 },
            },
            {
                question: "I can manage my own hearing aids.",
                answer: null,
                weight: { t: 0, f: 1 },
            },
            {
                question:
                    "I am able to manage my morning routine of getting dressed and ready for the day,",
                answer: null,
                weight: { t: 0, f: 1 },
            },
            {
                question:
                    "I am able to manage my evening routine of getting undressed and ready for bed.",
                answer: null,
                weight: { t: 0, f: 1 },
            },
            {
                question: "I need assistance with socks, shoes, or supports.",
                answer: null,
                weight: { t: 0, f: 1 },
            },
        ],
    },

    {
        index: 5,
        section: "Lifestyle",
        thresholds: { independent: 0, assisted: 3, memory: 10 },
        questions: [
            {
                question: "I do all my own shopping and errands.",
                answer: null,
                weight: { t: 0, f: 1 },
            },
            {
                question: "I am no longer allowed to have a driver’s license.",
                answer: null,
                weight: { t: 1, f: 0 },
            },
            {
                question: "I can leave my home without getting lost or confused.",
                answer: null,
                weight: { t: 0, f: 11 },
            },
            {
                question: "I am able to manage my own finances and cash.",
                answer: null,
                weight: { t: 0, f: 1 },
            },
            {
                question: "I repeat myself or fixate on topics.",
                answer: null,
                weight: { t: 5, f: 0 },
            },
            { question: "I use a cellular phone.", answer: null, weight: { t: 0, f: 1 } },
            {
                question: "I have been the victim of fraud or other crimes.",
                answer: null,
                weight: { t: 1, f: 0 },
            },
            {
                question: "I say or do things that are considered inappropriate.",
                answer: null,
                weight: { t: 0, f: 5 },
            },
            {
                question: "I wash and fold my own laundry.",
                answer: null,
                weight: { t: 0, f: 1 },
            },
            {
                question: "I require help with housekeeping.",
                answer: null,
                weight: { t: 1, f: 0 },
            },
            {
                question: "I clean my refrigerator and remove my garbage regularly.",
                answer: null,
                weight: { t: 0, f: 5 },
            },
            {
                question: "I am able to converse with others and be understood.",
                answer: null,
                weight: { t: 0, f: 5 },
            },
        ],
    },
];

const getByAnswer = (sections, answer) => {
    let items = [];

    if (sections) {
        Object.values(sections).forEach((section) => {
            section.questions.forEach((question) => {
                if (question.answer === answer) {
                    items.push(question.question);
                }
            });
        });
    }

    return items;
};

const updateUser = async (user, sections, submittedAssessment) => {
    if (user.uid) {
        await updateDocument("users", user.uid, {
            ...user,
            assessment: sections,
            submittedAssessment: submittedAssessment ? submittedAssessment : "",
        });
    }
};

const reducer = (state, action) => {
    switch (action.type) {
        case "LOADING":
            return { ...state, loading: true };

        case "INIT":
            if (action.user.assessment) {
                return { ...state, loading: false, user: action.user };
            } else {
                return {
                    ...state,
                    loading: false,
                    user: { ...action.user, assessment: defaultAssessment },
                };
            }

        case "UPDATE": {
            const sections = JSON.parse(JSON.stringify(state.user.assessment));
            const section = sections.find((s) => s.section === action.section);
            if (section) {
                const question = section.questions.find(
                    (q) => q.question === action.question
                );
                if (question) {
                    if (question.answer === action.answer) {
                        question.answer = null;
                    } else {
                        question.answer = action.answer;
                    }
                }
            }

            updateUser(state.user, sections, state.user.submittedAssessment);

            return { ...state, user: { ...state.user, assessment: sections } };
        }

        case "SUBMIT":
            updateUser(
                state.user,
                state.user.assessment,
                action.submit ? moment().format("MM/DD/YYYY") : ""
            );

            return {
                ...state,
                showNotAnswered: false,
                user: {
                    ...state.user,
                    submittedAssessment: action.submit ? moment().format("MM/DD/YYYY") : "",
                },
            };

        case "SHOW_NOT_ANSWERED":
            return { ...state, showNotAnswered: true };

        case "RESET":
            return {
                ...state,
                user: {
                    ...state.user,
                    assessment: defaultAssessment,
                    submittedAssessment: "",
                },
            };

        default:
            return state;
    }
};

const Assessment = () => {
    const router = useRouter();
    const auth = useAuth();
    const [openSignUp, setOpenSignUp] = useState(false);
    const [openSignIn, setOpenSignIn] = useState(false);
    const [state, dispatch] = useReducer(reducer, {
        loading: true,
        showNotAnswered: false,
        user: {
            submittedAssessment: "",
            assessment: defaultAssessment,
        },
    });

    const userId = auth.user ? auth.user.uid : "";

    useEffect(() => {
        const init = async (uid) => {
            dispatch({ type: "LOADING" });

            const user = await readDocument("users", uid);
            if (user) {
                dispatch({ type: "INIT", user });
            }
        };

        if (userId) {
            init(userId);
        }
    }, [userId]);

    const handleChange = (section, question, answer) => {
        dispatch({
            type: "UPDATE",
            section,
            question,
            answer,
        });
    };

    const handleSubmit = () => {
        let allFilled = true;

        state.user.assessment.forEach((section) => {
            section.questions.forEach((question) => {
                if (question.answer === null) {
                    allFilled = false;
                }
            });
        });

        if (allFilled) {
            dispatch({ type: "SUBMIT", submit: true });
            window.scrollTo(0, 0);
        } else {
            alert("Please answer all questions.");
            dispatch({ type: "SHOW_NOT_ANSWERED" });
        }
    };

    const handleDownloadPdf = () => {
        router.push("/resources/assessment-pdf");
        window.scrollTo(0, 0);
    };

    const handleUpdate = () => {
        dispatch({ type: "SUBMIT", submit: false });
        window.scrollTo(0, 0);
    };

    const handleReset = () => {
        if (window.confirm("Clear all selections?")) {
            dispatch({ type: "RESET" });
            window.scrollTo(0, 0);
        }
    };

    console.log("state", state);

    return (
        <div className="bg-gray-50">
            <HomeHeader fixed />
            <div className="container mx-auto pt-16 sm:pt-20 pb-4 px-4 sm:px-16">
                <Breadcrumbs
                    links={[
                        { name: "Resources", href: "/resources" },
                        {
                            name: "Assessment",
                            href: "/resources/assessment",
                        },
                    ]}
                />
            </div>
            <div>
                <div className="hidden sm:block">
                    <div className="relative w-full h-40 sm:h-72 bg-black">
                        <Image
                            loader={imageLoader}
                            src="https://cdn.purpledoorfinders.com/purpledoorfinders/right-fit-tools"
                            alt="purple-door-finders-articles-for-memory-care"
                            layout="fill"
                            objectFit="cover"
                            objectPosition="0px -200px"
                            priority
                            unoptimized={false}
                            className="opacity-70"
                        />

                        <div className="flex justify-center">
                            <h1 className="absolute px-8 top-12 sm:top-32 text-3xl sm:text-5xl text-white font-headline font-bold">
                                Assisted Living Needs Assessment
                            </h1>
                        </div>
                    </div>
                </div>

                <div className="sm:hidden">
                    <h1 className="px-4 text-3xl text-primary-500 font-headline font-bold">
                        Assisted Living Needs Assessment
                    </h1>
                </div>

                {userId === "" ? (
                    <div className="container mx-auto mt-4 py-8 px-4 sm:px-16 bg-gray-300 rounded-lg">
                        <h4 className="font-bold text-2xl">
                            You need to be registed and signed in to use this tool and save
                            the information.
                        </h4>

                        <div className="mt-4 flex items-center">
                            <Button
                                className=""
                                onClick={() => {
                                    setOpenSignUp(true);

                                    gtag("event", "conversion", {
                                        send_to: "AW-10778046073/z0kvCMDQspMaEPncr5Mo",
                                    });

                                    oaiq("measure", "registration_completed", {
                                        type: "customer_action",
                                    });
                                }}
                            >
                                Create Account
                            </Button>
                        </div>

                        <div className="mt-4">
                            Already have an account?
                            <buton
                                className="ml-4 text-purple-500 hover:underline"
                                onClick={() => setOpenSignIn(true)}
                            >
                                Sign In
                            </buton>
                        </div>
                    </div>
                ) : null}

                {(userId ? !state.loading : true) && !state.user.submittedAssessment ? (
                    <div className="container mx-auto py-12 px-4 sm:px-16">
                        <div>
                            <h2 className="text-3xl text-primary-500 font-bold font-headline">
                                Get Started
                            </h2>

                            <p className="mt-8">
                                Your personal Assessment is a tool that will help you
                                determine the best type of senior living for you:
                                Independent Living, Assisted Living, or Memory Care. If this
                                is your first time searching for senior living, their
                                differences may not be as apparent as you think. This short
                                2-minute survey will help you determine where to start your
                                search. This does not guarantee accurate placement. It
                                should be used as a guide in your decision-making process.
                            </p>

                            <p className="mt-4">
                                Upon completion of this tool, you can download your list of
                                preferences as a reference during your search, save to your
                                profile to access later and share, and download.
                            </p>

                            <hr className="my-8 border-black" />

                            <div className="flex flex-col space-y-8">
                                {state.user.assessment.map((section) => (
                                    <Section
                                        key={section.index}
                                        section={section}
                                        onChange={handleChange}
                                        showNotAnswered={state.showNotAnswered}
                                    />
                                ))}
                            </div>

                            <div className="mt-8 flex justify-end">
                                <Button
                                    className="uppercase"
                                    onClick={handleSubmit}
                                    disabled={!userId}
                                >
                                    Submit
                                </Button>
                            </div>
                        </div>
                    </div>
                ) : null}

                {userId && !state.loading && state.user.submittedAssessment ? (
                    <div className="container mx-auto py-12 px-4 sm:px-16">
                        <div>
                            <h2 className="text-3xl text-primary-500 font-bold font-headline">
                                You're All Done!
                            </h2>

                            <p className="mt-8">
                                Your personal Assessment is complete. You now have a
                                resource that helps guide to you for best senior and
                                assisted
                            </p>

                            <hr className="my-8 border-black" />

                            <div className="mt-8">
                                <Results result={state.user.assessment} />
                            </div>

                            <div className="mt-8 p-4 border-2 border-black">
                                <p>
                                    This Assisted Living Needs Assessment is a guide and
                                    tool for you to use when starting your senior and
                                    assisted living search. Each community will conduct
                                    their own assessment possibly with the advisement of
                                    physicians to determine the living needs of senior
                                    residents. This tool does not replace the assessments
                                    conducted by communities and physicians.
                                </p>
                            </div>

                            <div className="mt-8 flex justify-start space-x-2 sm:space-x-8">
                                <Button className="uppercase" onClick={handleDownloadPdf}>
                                    Download as PDF
                                </Button>

                                <Button className="uppercase" onClick={handleUpdate}>
                                    Update Selections
                                </Button>

                                <Button
                                    variant="outlined"
                                    className="uppercase"
                                    onClick={handleReset}
                                >
                                    Clear All Selections
                                </Button>
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>

            <SeoLinks />
            <HomeFooter />

            <SignUpSignIn
                openSignUp={openSignUp}
                openSignIn={openSignIn}
                setOpenSignUp={setOpenSignUp}
                setOpenSignIn={setOpenSignIn}
            />
        </div>
    );
};

const Section = ({ section, onChange, showNotAnswered }) => {
    return (
        <div className="">
            <h3 className="font-black">
                {section.index}. {section.section}
            </h3>

            <div className="mt-2 flex flex-col space-y-0">
                {section.questions.map((question, index) => (
                    <div key={question.question} className="px-4 py-3">
                        <div className="w-72 sm:w-96">{question.question}</div>

                        <div className="mt-2 flex space-x-16">
                            <Checkbox
                                id={`${section.index}-${index}-true`}
                                label="True"
                                name={question.question + "-true"}
                                value={question.answer === true}
                                onChange={() =>
                                    onChange(section.section, question.question, true)
                                }
                            />

                            <Checkbox
                                id={`${section.index}-${index}-false`}
                                label="False"
                                name={question.question + "-false"}
                                value={question.answer === false}
                                onChange={() =>
                                    onChange(section.section, question.question, false)
                                }
                            />

                            {showNotAnswered && question.answer === null && (
                                <div className="px-4 py-1 bg-red-500 text-white rounded">
                                    Please select True or False
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const Results = ({ result }) => {
    let mainResult = "independent";
    result.map((section) => {
        const score = section.questions.reduce(
            (acc, question) =>
                acc +
                (question.answer === true
                    ? question.weight.t
                    : question.answer === false
                    ? question.weight.f
                    : 0),
            0
        );

        console.log("score", score, section);

        section.score = score;

        if (score >= section.thresholds.memory) {
            section.result = "Memory Care";
        } else if (score >= section.thresholds.assisted) {
            section.result = "Assisted Living";
        } else {
            section.result = "Independent Living";
        }

        if (score >= section.thresholds.memory) {
            mainResult = "memory";
        } else if (mainResult !== "memory" && score >= section.thresholds.assisted) {
            mainResult = "assisted";
        }
    });

    return (
        <div>
            {mainResult === "independent" ? (
                <div>
                    <p className="font-black">
                        It is recommended that your senior living is INDEPENDENT LIVING.
                    </p>

                    <p className="mt-2">
                        The senior is able to take care of themselves, do self-care tasks,
                        prepare and manage their meals, and manage their own medications
                        without assistance. In addition, they are able to move independently
                        without the help of others and care for their own daily needs.
                        Therefore a community does not need to provide additional
                        assistance. However, knowing that aging will usually lead to needing
                        more assistance in the future, you may want to consider a community
                        that offers assisted living should you need it in the future or plan
                        for those needs in the future.
                    </p>
                </div>
            ) : null}

            {mainResult === "assisted" ? (
                <div>
                    <p className="font-black">
                        It is recommended that your senior living is ASSISTED LIVING.
                    </p>

                    <p className="mt-2">
                        The senior needs support and assistance with daily life tasks, such
                        as caring for themselves, staying mobile, preparing and managing
                        their meals, and managing their own medications without assistance.
                        Assisted living offers 24 hours support for daily support by trained
                        medical staff and caregivers. If there are signs of memory loss,
                        dementia, or early Alzheimer's diagnosis, it is advised to look for
                        assisted living communities that also have memory care available for
                        future needs.
                    </p>
                    <p className="mt-2">
                        Below you will find your results of all five sections of the
                        assessment. They may differ from the final recommendation, it is
                        advised that seniors plan for the continuum of their living needs.
                        So, you may score as independent living in some aspects of daily
                        life, however assistance may be needed for others and more
                        assistance will likely be needed in the future.
                    </p>
                </div>
            ) : null}

            {mainResult === "memory" ? (
                <div>
                    <p className="font-black">
                        It is recommended that your senior living is MEMORY CARE.
                    </p>

                    <p className="mt-2">
                        With memory loss, dementia, or Alzheimer's diagnosis, memory care
                        offers additional specialized support for seniors with
                        memory-related conditions and behaviors. Specially-trained
                        caregivers, nurses, and medical professionals are able to give the
                        extra attention necessary for caring for and supporting seniors with
                        such conditions.
                    </p>

                    <p className="mt-2">
                        Below you will find your results of all five sections of the
                        assessment. They may differ from the final recommendation, it is
                        advised that seniors plan for the continuum of their living needs.
                        So, you may score as assisted living in some aspects of daily life,
                        however memory care may be needed for others and more support will
                        likely be needed in the future.
                    </p>
                </div>
            ) : null}

            <div className="mt-8">
                <div className="flex space-x-2">
                    <div className="font-black">{result[0].section}:</div>
                    <div>Score: {result[0].score}</div>
                    <div> - {result[0].result}</div>
                </div>

                {result[0].result === "Independent Living" && (
                    <p>
                        When seniors are able to prepare meals and feed themselves, they are
                        likely able to live independent- ly. The ability to organize and
                        safely manage their needs for meals is a clear sign of cognitive
                        abilities to responsibility use kitchen appliances, planning meals,
                        and prepare food. Independent living skills such as food preparation
                        are a hallmark of the ability to live in an environment where
                        support for such daily needs is not required.
                    </p>
                )}

                {result[0].result === "Assisted Living" && (
                    <p>
                        As people age, the skills needed to prepare meals and feed
                        themselves independently are critical for maintaining a healthy and
                        safe lifestyle. When seniors need help with these daily life skills,
                        an environment that supports them is ideal. In assisted living,
                        services such as dining, food preparation, and even in-unit meal
                        delivery can support a senior in their daily lives. However, not
                        only can planning and preparing meals become difficult, but it can
                        also become unsafe.
                    </p>
                )}

                {result[0].result === "Memory Care" && (
                    <p>
                        When a senior is cognitively impaired due to memory issues,
                        dementia, or Alzheimer's, it is no longer safe for them to be
                        preparing meals and using kitchen appliances. The chance that a
                        stove is left on, water running, and so forth becomes increasingly
                        more likely if it hasn’t already happened. This poses a health risk
                        to the seniors as well as their living environment. It is also
                        likely that the stress of prepar- ing food is increasing as well. If
                        the ability to remember the basics of food preparation is impaired,
                        a facility offering memory care is likely the best option. The units
                        in memory care will usually not have a functioning kitchen to avoid
                        any problems that may arise, and all meals will be prepared for the
                        resident.
                    </p>
                )}
            </div>

            <div className="mt-8">
                <div className="flex space-x-2">
                    <div className="font-black">{result[1].section}:</div>
                    <div>Score: {result[1].score}</div>
                    <div> - {result[1].result}</div>
                </div>

                {result[1].result === "Independent Living" && (
                    <p>
                        Medications are critical for seniors as they age. The ability to
                        completely manage, organize and administer medications ensures their
                        health and avoids possible problems and stress. Managing medications
                        independently is a clear indicator that the senior has the cognitive
                        abilities to live inde- pendently.
                    </p>
                )}

                {result[1].result === "Assisted Living" && (
                    <p>
                        The health and safety of seniors are most important in terms of
                        managing medications. Many seniors have multiple health needs that
                        are addressed by medications they take, whether pills, vitamins, or
                        daily injections, such as diabetes. It is not uncommon for seniors
                        to have difficulty remembering what they are when to take and how
                        much to take. Assisted living offers help with organizing, tracking,
                        and administering numerous medications, so the resident is ensured
                        to stay healthy and safe. In addition, most communities have medical
                        professionals’ and nurses' oversight so that even the efficacy of
                        medications can be monitored on a daily basis and medical care and
                        attention given when needed.
                    </p>
                )}

                {result[1].result === "Memory Care" && (
                    <p>
                        When a senior starts to forget to take medications, refuses
                        medication, and mismanages their medi- cations due to confusion,
                        memory impairment, dementia, or Alzheimer's, memory care can offer
                        additional support to ensure that a senior is safe and healthy.
                        Memory care also has trained staff that specifically work with
                        memory care residents to address the challenges that are unique to
                        residents with such impairment. There are medications that are
                        unique to treating memory-related conditions that will be used to
                        manage them as well.
                    </p>
                )}
            </div>

            <div className="mt-8">
                <div className="flex space-x-2">
                    <div className="font-black">{result[2].section}:</div>
                    <div>Score: {result[2].score}</div>
                    <div> - {result[2].result}</div>
                </div>

                {result[2].result === "Independent Living" && (
                    <p>
                        Getting around and being mobility is key to living independently as
                        a senior. The senior is able to perform most basic activities needed
                        in daily life, such as getting out of bed, walking, standing without
                        support, and navigating their living space without assistance. They
                        should also be able to physically care for themselves, such as
                        safely bathing, getting in and out of the shower, and washing their
                        bodies, all of which require balance, coordination, and strength.
                    </p>
                )}

                {result[2].result === "Assisted Living" && (
                    <p>
                        Our bodies weaken as we age, and the independence to move, walk, get
                        out of bed, balance, and perform daily tasks can become more
                        difficult. Assisted living communities offer support for such daily
                        activities with trained staff available 24 hours per day for all
                        mobility needs. This support is necessary to ensure the senior is
                        able to continue being active, engaged, and safe.
                    </p>
                )}

                {result[2].result === "Memory Care" && (
                    <p>
                        Mobility is not always a key indicator of memory care needs.
                        However, with memory impairment and related conditions such as
                        Alzheimer's, judgment is impaired, and the ability to understand and
                        recall physical abilities can become hazardous. Sitting for extended
                        periods of time and sleeplessness are signs that memory care may be
                        needed.
                    </p>
                )}
            </div>

            <div className="mt-8">
                <div className="flex space-x-2">
                    <div className="font-black">{result[3].section}:</div>
                    <div>Score: {result[3].score}</div>
                    <div> - {result[3].result}</div>
                </div>

                {result[3].result === "Independent Living" && (
                    <p>
                        If a senior is able to take care of their daily personal needs, they
                        are safe to live independently. This means they are able to shower,
                        use the bathroom, dress themselves, and are mentally stable enough
                        to conduct them regularly without help.
                    </p>
                )}

                {result[3].result === "Assisted Living" && (
                    <p>
                        Along with mobility, the ability to take care of personal on a daily
                        basis is a natural part of aging. Whether due to balance,
                        flexibility, or other impairments, assistance may be needed for
                        daily activities such as bathing, using the bathroom, getting
                        dressed, managing hearing aids, and other essential tasks needed to
                        care for oneself. Assisted living provides support in those
                        activities at scheduled times throughout the day that keeps seniors
                        safe, well cared for, and engaged.
                    </p>
                )}

                {result[3].result === "Memory Care" && (
                    <p>
                        As memory issues develop, the ability to care for oneself decreases.
                        Whether the ability to care for themselves, remember to do, or even
                        how to care for themselves, daily care needs become more prevalent.
                        These daily care needs can sometimes be overlooked in seniors if
                        they do have the proper support. Memory care specifically addresses
                        these daily needs to maintain routine and dignity in daily personal
                        care needs.
                    </p>
                )}
            </div>

            <div className="mt-8">
                <div className="flex space-x-2">
                    <div className="font-black">{result[4].section}:</div>
                    <div>Score: {result[4].score}</div>
                    <div> - {result[4].result}</div>
                </div>

                {result[4].result === "Independent Living" && (
                    <p>
                        If a senior is able to conduct basic living activities independently
                        without oversight, independent living is their recommended
                        community. They should be able to provide basic necessities for
                        themselves, have predict- able, stable behavior, keep their homes
                        clean, conduct basic chores and do them safely and confidently.
                    </p>
                )}

                {result[4].result === "Assisted Living" && (
                    <p>
                        The aid provided in assisted living allows seniors to have the
                        support they need to live safely and comfortably. For example, there
                        is assistance in transportation, shopping, and caring for their
                        living spaces. In addition, there is usually garbage removal,
                        cleaning, chore support, and other supervision. Assisted living is
                        the right choice for seniors who need extra assistance in performing
                        daily living tasks.
                    </p>
                )}

                {result[4].result === "Memory Care" && (
                    <p>
                        A hallmark of memory care is not only having the ability to carry
                        out daily activities but also doing so confidently with an even
                        temperament. Memory loss and related conditions such as dementia and
                        Alzheimers can lead to confusion, stress, and mismanagement of such
                        daily tasks. Seniors with memory loss may want independence in their
                        areas of life management but have difficulties conduct- ing them
                        correctly and remembering to do them, and may cause harm in trying
                        to do them. Memory care offers aid to seniors in managing lifestyle
                        tasks in a supportive and safe way.
                    </p>
                )}
            </div>
        </div>
    );
};

export const getStaticProps = async () => {
    return {
        props: {
            meta: {
                title: "Assisted Living Needs Assessment Checklist",
                description:
                    "If this is your first time searching for senior living, their differences may not be as apparent as you think. Your personal Assessment is a tool that will help you determine the best type of senior living for you.",
            },
        },
    };
};

export default Assessment;
